import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { IconCube } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import {
  DailyProgressItemRequestDto,
  DesignModelDto,
  WbsAccountResponse,
} from 'src/api/FiretrolApiClient';
import {
  DailyDesignProgressTarget,
  useDailyDesignProgressSummary,
} from 'src/api/queries/dailys/useDailyDesignProgressSummary';
import { useJobWbs } from 'src/api/queries/wbs/useJobWbs';
import { useDesignModels } from 'src/api/queries/designModels/useDesignModels';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { ProgressViewerScopes } from 'src/data/scopes';
import { useAuth } from 'src/hooks/useAuth';
import { DailyProgressViewerModal } from 'src/routes/Operations/Daily/components/DailyProgressViewerModal';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  convertAccountsToTreeData,
  hasProgressManagedDescendant,
} from 'src/utils';
import { CTCLinkIndicator } from 'src/routes/Jobs/WBS/components/CTCLinkIndicator';
import { ProgressManagedIndicator } from 'src/routes/Jobs/WBS/components/ProgressManagedIndicator';
import { WbsTreeData } from 'src/routes/Jobs/WBS/components/types';

interface ProgressRowData {
  id: string;
  name: string;
  depth: number;
  isLeaf: boolean;
  progressManaged: boolean;
  estimatedQuantity: number;
  actualQuantity: number;
  unitOfMeasure?: string;
  node: WbsTreeData;
  subRows?: ProgressRowData[];
}

interface PhysicalProgressInputProps {
  jobId: number;
  initialProgress: DailyProgressItemRequestDto[];
  onProgressChange?: (progress: DailyProgressItemRequestDto[]) => void;
  viewOnly?: boolean;
  jobLabel?: string;
  /** Which installed Progress viewer items count toward progress-managed rows. */
  designProgressRequest?: DailyDesignProgressTarget;
  /** Enables the Progress viewer button; receives the ids of each confirmed status save. */
  onStatusesSaved?: (progressItemIds: string[]) => void;
}

const NO_SUMMARY_REQUEST: DailyDesignProgressTarget = {};

const MODIFIED_INPUT_STYLES = {
  input: { fontWeight: 700, color: 'black' },
};

const formatValue = (value: number) => {
  return Number(value).toFixed(0);
};

const calculateAggregatedValues = (
  nodes: ProgressRowData[],
): {
  estimatedQuantity: number;
  actualQuantity: number;
} => {
  const leafNodes = nodes.filter((node) => node.isLeaf);

  if (leafNodes.length === 0) {
    return {
      estimatedQuantity: 0,
      actualQuantity: 0,
    };
  }

  const summed = leafNodes.reduce(
    (acc, node) => {
      acc.estimatedQuantity += node.estimatedQuantity;
      acc.actualQuantity += node.actualQuantity;
      return acc;
    },
    {
      estimatedQuantity: 0,
      actualQuantity: 0,
    },
  );

  return summed;
};

/**
 * Maps each progress-managed leaf to its drawing. The leaf's parent is the
 * drawing account, named after the drawing and parented by the drawing's WBS
 * root account.
 */
const resolveLeafDrawingIds = (
  accounts: WbsAccountResponse[],
  designModels: DesignModelDto[],
): Map<string, string> => {
  const accountsById = new Map<string, WbsAccountResponse>();
  const collect = (nodes: WbsAccountResponse[]) =>
    nodes.forEach((account) => {
      accountsById.set(account.id, account);
      collect(account.inverseParent ?? []);
    });
  collect(accounts);

  const drawingIds = new Map<string, string>();
  accountsById.forEach((account) => {
    if (!account.progressManaged || !account.parentId) return;
    const drawingAccount = accountsById.get(account.parentId);
    if (!drawingAccount?.parentId) return;
    const drawing = designModels.find(
      (model) =>
        model.wbsAccountId === drawingAccount.parentId &&
        model.name === drawingAccount.name,
    );
    if (drawing?.id) drawingIds.set(account.id, drawing.id);
  });
  return drawingIds;
};

const transformWbsToTableData = (
  nodes: WbsTreeData[],
  depth = 0,
): ProgressRowData[] => {
  const rows: ProgressRowData[] = [];

  nodes.forEach((node) => {
    const shouldInclude =
      node.apply || (node.children && node.children.length > 0);
    if (!shouldInclude) return;

    const isLeaf = node.apply && (!node.children || node.children.length === 0);

    if (isLeaf) {
      const estimatedQuantity = Number(node.currentQuantityEstimate);
      const actualQuantity = Number(node.currentQuantityActual);

      rows.push({
        id: node.id,
        name: node.name,
        depth,
        isLeaf: true,
        progressManaged: node.progressManaged === true,
        estimatedQuantity,
        actualQuantity,
        unitOfMeasure: node.unitOfMeasure,
        node,
      });
    } else {
      const childRows = node.children
        ? transformWbsToTableData(node.children, depth + 1)
        : [];

      const aggregated = calculateAggregatedValues(childRows);

      rows.push({
        id: node.id,
        name: node.name,
        depth,
        isLeaf: false,
        progressManaged: false,
        estimatedQuantity: aggregated.estimatedQuantity,
        actualQuantity: aggregated.actualQuantity,
        unitOfMeasure: node.unitOfMeasure,
        node,
        subRows: childRows,
      });
    }
  });

  return rows;
};

export const PhysicalProgressInput: React.FC<PhysicalProgressInputProps> = ({
  jobId,
  initialProgress,
  onProgressChange,
  viewOnly = false,
  jobLabel,
  designProgressRequest = NO_SUMMARY_REQUEST,
  onStatusesSaved,
}) => {
  const [values, setValues] =
    React.useState<DailyProgressItemRequestDto[]>(initialProgress);
  // initialProgress echoes onProgressChange back from the form, so the
  // baseline for the modified-value emphasis is captured once on mount.
  const [initialValues] = React.useState(
    () => new Map(initialProgress.map((p) => [p.wbsAccountId, p.value])),
  );
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerDrawingId, setViewerDrawingId] = React.useState<string>();
  const { data: wbsData, isLoading, isError, refetch } = useJobWbs(jobId);
  const { checkFeatureAllowed } = useAuth();
  const canOpenViewer =
    !viewOnly && !!onStatusesSaved && checkFeatureAllowed(ProgressViewerScopes);

  const { data: designModels } = useDesignModels(
    canOpenViewer ? jobId : undefined,
  );
  const leafDrawingIds = useMemo(
    () =>
      wbsData && designModels
        ? resolveLeafDrawingIds(wbsData.wbsAccount, designModels)
        : new Map<string, string>(),
    [wbsData, designModels],
  );

  const { data: designProgressSummary } = useDailyDesignProgressSummary(
    jobId,
    designProgressRequest,
  );
  const designProgressQuantities = useMemo(
    () =>
      new Map(
        (designProgressSummary ?? []).map((s) => [s.wbsAccountId, s.quantity]),
      ),
    [designProgressSummary],
  );
  // Each viewer save changes the summary request, so the baseline for the
  // modified-value emphasis is the first summary that arrives.
  const [initialDesignQuantities, setInitialDesignQuantities] =
    React.useState<Map<string, number>>();
  React.useEffect(() => {
    if (designProgressSummary && !initialDesignQuantities) {
      setInitialDesignQuantities(designProgressQuantities);
    }
  }, [
    designProgressSummary,
    designProgressQuantities,
    initialDesignQuantities,
  ]);

  const wbs = useMemo(
    () =>
      wbsData
        ? convertAccountsToTreeData(
            wbsData.wbsAccount,
            (account) =>
              (!account.archived &&
                account.active &&
                account.apply &&
                account.accountType === 'labor' &&
                !account.timesheetsOnly) ||
              (account.children && account.children.length > 0),
          )
        : [],
    [wbsData],
  );

  // Charge codes added to the WBS after this daily was started need a zeroed
  // row, but a row the user already entered must keep its value.
  React.useEffect(() => {
    if (!wbsData) return;
    const leafNodes: WbsTreeData[] = [];
    const traverseLeafNodes = (nodes: WbsTreeData[]) => {
      nodes.forEach((node) => {
        if (
          node.apply &&
          !node.timesheetsOnly &&
          !node.progressManaged &&
          (!node.children || node.children.length === 0)
        ) {
          leafNodes.push(node);
        } else if (node.children) {
          traverseLeafNodes(node.children);
        }
      });
    };
    traverseLeafNodes(wbs);

    setValues((prev) =>
      leafNodes.map(
        (t) =>
          prev.find((v) => v.wbsAccountId === t.id) ?? {
            wbsAccountId: t.id,
            item: t.name,
            value: 0,
          },
      ),
    );
  }, [wbs, wbsData]);

  React.useEffect(() => {
    if (onProgressChange) {
      onProgressChange(values);
    }
  }, [values]);

  const updateValue = (wbsAccountId: string, value: number) => {
    setValues((prevState) => {
      return prevState.map((i) => {
        if (i.wbsAccountId === wbsAccountId) {
          return {
            ...i,
            value,
          };
        }
        return i;
      });
    });
  };

  const onFocus = (e: any) => {
    e.target.select();
  };

  const tableData = useMemo(() => {
    return transformWbsToTableData(wbs);
  }, [wbs]);
  const hasProgressManaged = useMemo(
    () => wbs.some((n) => n.progressManaged || hasProgressManagedDescendant(n)),
    [wbs],
  );

  const columns = useMemo<MRT_ColumnDef<ProgressRowData>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Charge Code',
        Cell: ({ row }) => (
          <Group gap="xs" align="center">
            <Text c="black">{row.original.name}</Text>
            <CTCLinkIndicator node={row.original.node} size="xs" />
            <ProgressManagedIndicator node={row.original.node} size="xs" />
          </Group>
        ),
      },
      {
        id: 'estimatedQuantity',
        size: 150,
        accessorFn: (row) => row.estimatedQuantity,
        header: 'Estimated Qty',
        Cell: ({ cell }) => (
          <Text c="black">{formatValue(cell.getValue<number>())}</Text>
        ),
      },
      {
        id: 'actualQuantity',
        size: 150,
        accessorFn: (row) => row.actualQuantity,
        header: 'Installed Qty',
        Cell: ({ cell }) => (
          <Text c="black">{formatValue(cell.getValue<number>())}</Text>
        ),
      },
      {
        id: 'inputQuantity',
        size: 150,
        header: 'Progress Value',
        Cell: ({ row }) => {
          if (!row.original.isLeaf) {
            return <Text c="dimmed">-</Text>;
          }

          if (row.original.progressManaged) {
            const unit = row.original.unitOfMeasure;
            const current = designProgressQuantities.get(row.original.id) ?? 0;
            const quantity = formatValue(current);
            const modified =
              !!initialDesignQuantities &&
              current !== (initialDesignQuantities.get(row.original.id) ?? 0);
            return (
              <Group gap="xs" wrap="nowrap">
                {canOpenViewer && (
                  <BasicButton
                    size="compact-xs"
                    variant="light"
                    leftSection={<IconCube size={14} />}
                    aria-label="Open Progress viewer"
                    onClick={() => {
                      setViewerDrawingId(leafDrawingIds.get(row.original.id));
                      setViewerOpen(true);
                    }}
                  >
                    Open
                  </BasicButton>
                )}
                <Text
                  c={modified ? 'black' : undefined}
                  fw={modified ? 700 : undefined}
                >
                  {unit ? `${quantity} ${unit}` : quantity}
                </Text>
              </Group>
            );
          }

          const target = values.find((v) => v.wbsAccountId === row.original.id);
          if (!target) return <Text c="dimmed">-</Text>;

          if (viewOnly) {
            return <Text>{formatValue(target.value)}</Text>;
          }

          const modified =
            (target.value ?? 0) !== (initialValues.get(row.original.id) ?? 0);

          return (
            <NumberInput
              styles={modified ? MODIFIED_INPUT_STYLES : undefined}
              onFocus={onFocus}
              min={0}
              value={target.value}
              onChange={(value) => {
                if (!isNaN(Number(value))) {
                  updateValue(row.original.id, Number(value));
                }
              }}
            />
          );
        },
      },
    ],
    [
      values,
      viewOnly,
      designProgressQuantities,
      initialDesignQuantities,
      canOpenViewer,
      leafDrawingIds,
      initialValues,
    ],
  );

  const tableState = useTableState({
    identifier: 'physical-progress-input-table',
    columns: columns,
  });

  const tableExports = useTableExports<ProgressRowData>(
    'daily-physical-progress',
    (data) => {
      const flattenData = (items: ProgressRowData[], prefix = ''): any[] => {
        return items.flatMap((item) => {
          const current = {
            ['Name']: prefix + item.name,
            ['Estimated Quantity']: item.estimatedQuantity,
            ['Actual Quantity']: item.actualQuantity,
            ['Completion %']:
              (item.actualQuantity / item.estimatedQuantity) * 100 || 0,
          };
          if (item.subRows && item.subRows.length > 0) {
            return [current, ...flattenData(item.subRows, prefix + '  ')];
          }
          return [current];
        });
      };
      return flattenData(data);
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableExpanding: true,
      enableExpandAll: true,
      enableBottomToolbar: false,
      enableTopToolbar: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      enablePagination: false,
      columns: columns,
      data: tableData,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableRowActions: false,
      enableClickToCopy: false,
      initialState: {
        expanded: true,
      },
    },
    getRowId: (row) => row.id,
    totalCount: tableData.length,
    isLoading,
    isFetching: false,
    isError,
    ...tableExports,
  });

  if (isLoading) {
    return (
      <Stack align="center" mt="md">
        <Loader />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack gap="xs">
        <Text c="red">Failed to load charge codes.</Text>
        <Group>
          <Text size="sm">
            You may continue by selecting "No Progress" above.
          </Text>
          <BasicButton size="compact-sm" onClick={() => refetch()}>
            Retry
          </BasicButton>
        </Group>
      </Stack>
    );
  }

  if (values.length === 0 && !hasProgressManaged) {
    return (
      <Text>
        No active charge codes. Update charge codes in the job WBS settings.
      </Text>
    );
  }

  return (
    <>
      {table}
      {canOpenViewer && onStatusesSaved && (
        <DailyProgressViewerModal
          opened={viewerOpen}
          onClose={() => setViewerOpen(false)}
          jobId={jobId}
          jobLabel={jobLabel}
          onStatusesSaved={onStatusesSaved}
          initialDrawingId={viewerDrawingId}
        />
      )}
    </>
  );
};
